import { useEffect } from "react"

import { Empty, Flex, Modal, Spin, Tag, Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"

import Icon from "../../../../../Icon"
import { resetProfileUpdate, selectProfileUpdate } from "../../redux/requestsReceipts.slice"
import { getReceiptProfileUpdateDiff } from "../../redux/requestsReceipts.thunks"

import styles from "./styles.module.less"

const { Text, Link } = Typography

const EMPTY = "—"

/**
 * ProfileUpdateModal — Eye icon на profile-update рядку → `Requested profile update` (CHA1-1795).
 *
 * На відміну від `ProfileDetailsModal` (одна колонка VALUE, дескрипторний рядок), тут три
 * колонки: FIELD / CURRENT / PROPOSED. Секції ті самі:
 *   - Top (Program, Business account, User, Website) — ідентифікація, ніколи не змінюється
 *   - Step 1 · Business info (3)
 *   - Step 2 · Business details (9)
 *   - Step 4 · Support & legal (6)
 *
 * `changed` рахує бекенд із двох збережених значень — фронт лише підсвічує. Порівнювати тут
 * самостійно не можна: правила порівняння (напр. логотип звіряється за файлом, а не за id
 * вкладення) живуть у домені, а не в рендері.
 */
const ProfileUpdateModal = ({ open, id, onClose }) => {
  const dispatch = useDispatch()
  const { fetching, data, error } = useSelector(selectProfileUpdate)

  useEffect(() => {
    if (open && id != null) {
      dispatch(getReceiptProfileUpdateDiff(id))
    }
    if (!open) {
      dispatch(resetProfileUpdate())
    }
  }, [open, id, dispatch])

  const record = data || {}
  const profile = record.receipt_profile || {}
  const businessAccountName =
    record?.business_account?.business_account || record?.business_account?.name

  const renderValue = (value, { isLink, muted } = {}) => {
    if (!value) return <Text type="secondary">{EMPTY}</Text>

    const className = muted ? styles.outdated : undefined

    if (isLink && !muted) {
      return (
        <Flex gap={8} align="center">
          <Link href={value} target="_blank" onClick={(e) => e.stopPropagation()}>
            {value}
          </Link>
          <Text copyable={{ text: value, tooltips: false }} />
        </Flex>
      )
    }

    return (
      <Flex gap={8} align="center">
        <Text className={className}>{value}</Text>
        <Text copyable={{ text: String(value), tooltips: false }} />
      </Flex>
    )
  }

  const renderLogoValue = (logo, { muted } = {}) => {
    if (!logo || !logo.file_name) return <Text type="secondary">{EMPTY}</Text>

    return (
      <Flex gap={8} align="center">
        <div className={styles.logoThumb} aria-hidden>
          {logo.file_url ? (
            <img src={logo.file_url} alt="" />
          ) : (
            <Icon type="image" style={{ opacity: 0.5 }} />
          )}
        </div>
        <Text className={muted ? styles.outdated : undefined}>{logo.file_name}</Text>
        <Text copyable={{ text: logo.file_name, tooltips: false }} />
      </Flex>
    )
  }

  /**
   * Один рядок діфу. `render` дістає обидві сторони з того самого ключа `receipt_profile`,
   * тому додати поле — це один запис у списку нижче, а не два способи його прочитати.
   */
  const diffRow = (field, label, { isLink, isLogo } = {}) => {
    const cell = profile[field] || {}
    const changed = Boolean(cell.changed)
    const render = isLogo ? renderLogoValue : renderValue

    return {
      testId: field,
      field: label,
      changed,
      // Стара сторона приглушена лише коли є що порівнювати: у незміненому рядку обидві
      // колонки — це одне й те саме актуальне значення.
      current: render(cell.current, { isLink, muted: changed }),
      proposed: render(cell.proposed, { isLink })
    }
  }

  const rows = [
    diffRow("program", "Program"),
    {
      testId: "business_account",
      field: "Business account",
      changed: false,
      current: renderValue(businessAccountName),
      proposed: renderValue(businessAccountName)
    },
    diffRow("user_full_name", "User full name"),
    diffRow("user_email", "User email"),
    diffRow("website", "Website", { isLink: true }),

    { section: "STEP 1 · BUSINESS INFO (3)" },
    diffRow("business_name", "Business name"),
    diffRow("business_website", "Business website", { isLink: true }),
    diffRow("business_description", "Business description"),

    { section: "STEP 2 · BUSINESS DETAILS (9)" },
    diffRow("business_logo", "Business logo", { isLogo: true }),
    diffRow("business_address", "Business address"),
    diffRow("city", "City"),
    diffRow("postal_code", "Postal code"),
    diffRow("country", "Country"),
    diffRow("country_code", "Country code"),
    diffRow("state_region", "State/Region"),
    diffRow("product_name", "Product/service name"),
    diffRow("product_description", "Product/service description"),

    { section: "STEP 4 · SUPPORT & LEGAL (6)" },
    diffRow("support_phone", "Support phone"),
    diffRow("support_email", "Support email"),
    diffRow("support_website", "Support website", { isLink: true }),
    diffRow("terms_url", "Terms & conditions", { isLink: true }),
    diffRow("refund_policy_url", "Refund policy", { isLink: true }),
    diffRow("cancellation_policy_url", "Cancellation policy", { isLink: true })
  ]

  return (
    <Modal
      open={open}
      title="Requested profile update"
      onCancel={onClose}
      footer={null}
      width={900}
      centered
      destroyOnHidden
      data-testid="deflect-admin-profile-update-modal"
    >
      {fetching && !data ? (
        <Flex align="center" justify="center" style={{ minHeight: 320 }}>
          <Spin size="large" />
        </Flex>
      ) : error ? (
        <Flex align="center" justify="center" style={{ minHeight: 320 }}>
          <Empty description="Failed to load the requested profile update." />
        </Flex>
      ) : (
        <div className={styles.table}>
          <div className={styles.headerRow}>
            <div className={styles.field}>FIELD</div>
            <div className={styles.value}>CURRENT</div>
            <div className={styles.value}>PROPOSED</div>
          </div>
          {rows.map((row, i) =>
            row.section ? (
              <div key={`s-${i}`} className={styles.sectionHeader}>
                {row.section}
              </div>
            ) : (
              <div
                key={row.field}
                className={row.changed ? `${styles.row} ${styles.changedRow}` : styles.row}
                data-testid={`deflect-admin-profile-update-row-${row.testId}`}
              >
                <div className={styles.field}>{row.field}</div>
                <div className={styles.value}>{row.current}</div>
                <div className={styles.value}>
                  <Flex gap={8} align="center" wrap="wrap">
                    {row.proposed}
                    {row.changed && (
                      <Tag
                        color="success"
                        bordered={false}
                        data-testid={`deflect-admin-profile-update-changed-${row.testId}`}
                      >
                        Changed
                      </Tag>
                    )}
                  </Flex>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </Modal>
  )
}

export default ProfileUpdateModal
